import { useEffect, useState } from "react";
import { apiRequest, erpApi, useDevelopmentFixtures } from "../../api/client";
import { useErpData } from "../../data/DataProvider";
import { mexicoDateKey } from "../../data/format";
import type { ServiceType } from "../../data/types";

interface Upcoming {
  id: string;
  service: ServiceType;
  customer: string;
  date: string;
  time: string;
  guests: number;
}
interface Overview {
  counts: Partial<Record<ServiceType, { confirmed: number; pending: number }>>;
  upcoming: Upcoming[];
}
export function useOperationOverview(service?: ServiceType) {
  const { snapshot } = useErpData();
  const [result, setResult] = useState<Overview | null>(null);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (useDevelopmentFixtures) return;
    let active = true;
    setError("");
    setResult(null);
    const services: ServiceType[] = service
      ? [service]
      : ["balloon", "tour", "school"];
    const until = new Date(Date.now() + 60 * 86400000)
      .toISOString()
      .slice(0, 10);
    Promise.all([
      Promise.all(
        services.map(async (value) => {
          const [confirmed, pending] = await Promise.all(
            ["confirmed", "pending"].map((status) =>
              apiRequest<{ pagination: { total: number } }>(
                `/api/v1/bookings?service_type=${value}&status=${status}&page=1&per_page=1`,
              ),
            ),
          );
          return [
            value,
            {
              confirmed: confirmed!.pagination.total,
              pending: pending!.pagination.total,
            },
          ] as const;
        }),
      ),
      erpApi.agenda(mexicoDateKey(), until, service),
    ])
      .then(([counts, agenda]) => {
        if (active)
          setResult({
            counts: Object.fromEntries(counts),
            upcoming: agenda
              .filter(
                (item) => !item.tentative && item.sourceKind === "booking",
              )
              .map((item) => ({
                id: item.sourceId,
                service: item.service,
                customer: item.subtitle || item.title,
                date: item.date,
                time: item.time ?? "Horario por definir",
                guests: item.confirmed,
              }))
              .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time))
              .slice(0, 6),
          });
      })
      .catch((caught) => {
        if (active)
          setError(
            caught instanceof Error
              ? caught.message
              : "No pudimos cargar el resumen operativo.",
          );
      });
    return () => {
      active = false;
    };
  }, [service, revision, snapshot]);
  const fixture: Overview | null =
    useDevelopmentFixtures && snapshot
      ? {
          counts: Object.fromEntries(
            (["balloon", "tour", "school"] as const).map((value) => [
              value,
              {
                confirmed: snapshot.bookings.filter(
                  (b) => b.service === value && b.status === "confirmed",
                ).length,
                pending: snapshot.bookings.filter(
                  (b) =>
                    b.service === value &&
                    ["holding", "pending_payment"].includes(b.status),
                ).length,
              },
            ]),
          ),
          upcoming: snapshot.bookings
            .filter(
              (b) =>
                (!service || b.service === service) &&
                b.status === "confirmed" &&
                b.date >= mexicoDateKey(),
            )
            .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time))
            .slice(0, 6)
            .map((b) => ({
              id: b.id,
              service: b.service,
              customer: b.customer,
              date: b.date,
              time: b.time,
              guests: b.guests,
            })),
        }
      : null;
  return {
    data: fixture ?? result,
    error,
    refresh: () => setRevision((v) => v + 1),
  };
}
